import { useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import {
    useGroupBy,
    useTrackMapTransform,
} from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/hooks/useAllAudioTracks';
import { SoundTracksListProps } from '@/scenarios/legacy-player/components/PlayerSkin/parts/SoundTracksList/SoundTrackList.types';
import { useDisableWhenAlone } from '@/scenarios/legacy-player/components/PlayerSkin/parts/Tracks/hooks/useDisableWhenAlone';

import { Quality, Track, useAudioTrackContext } from '../Tracks.types';

const getSoundQuality = (intl: IntlShape): Record<Quality, string> => ({
    STEREO: intl.formatMessage({
        id: 'components.PlayerSkin.bbc45d9b35436f5f0ed9e33499efcea9',

        defaultMessage: 'Stereo',
        description: 'Текст для стерео звука в списке озвучек',
    }),
    SURROUND_51: intl.formatMessage({
        id: 'components.PlayerSkin.c7468ad58db810e54e555de2b0b51957',

        defaultMessage: '5.1 surround sound',
        description: 'Текст для 5.1 звука в списке озвучек',
    }),
    OTHER: intl.formatMessage({
        id: 'components.PlayerSkin.6311ae17c1ee52b36e68aaf4ad066387',

        defaultMessage: 'Other',
        description: 'Текст для неизвестного качества звука в списке озвучек',
    }),
});

const getName = (intl: IntlShape, soundName: string | undefined) => {
    const soundQuality = getSoundQuality(intl);
    if (soundName === undefined) {
        return soundQuality.OTHER;
    }
    return soundName in soundQuality ? soundQuality[soundName as Quality] : soundName;
};

export const trackKey: (t: Track) => string = (t) =>
    t.info ? `${t.info.audioGroupId}` : `${t.lang} + ${t.name}`;

interface Params {
    onSelectAudioTrack: (track: Track) => void;
}

export const useSoundQualityTracks: (params: Params) => {
    currentTrack: Track | undefined;
    qualityTracks: readonly Track[];
    handleSelectSoundTrack: SoundTracksListProps['handleSelectSoundTrack'];
} = ({ onSelectAudioTrack }) => {
    const intl = useIntl();

    const { currentAudioTrack, allAudioTracksMap: allAudioTracksMapRaw } = useAudioTrackContext();

    const allAudioTracksMap = useTrackMapTransform(allAudioTracksMapRaw, (t) => ({
        ...t,
        name: `${getName(intl, t.info?.quality)}`,
    }));

    const grouped = useGroupBy(allAudioTracksMap, trackKey);

    const handlerWrapper = useCallback<SoundTracksListProps['handleSelectSoundTrack']>(
        (track) => {
            onSelectAudioTrack(
                Array.from(allAudioTracksMapRaw.values()).find((t) => t.value === track.value)!
            );
        },
        [allAudioTracksMapRaw, onSelectAudioTrack]
    );

    return {
        currentTrack: currentAudioTrack ? allAudioTracksMap.get(currentAudioTrack) : undefined,
        qualityTracks: useDisableWhenAlone(
            currentAudioTrack
                ? grouped.get(trackKey(allAudioTracksMap.get(currentAudioTrack)!))!
                : []
        ),
        handleSelectSoundTrack: handlerWrapper,
    };
};
